/* Ronaq — SVG illustrations for the dining set and niche configurators.
 * window.RonaqDraw.dining(opts) / .niche(opts) / .chairIcon(style, fabric, wood) return SVG strings. */
(function () {
  let uidSeed = 0;

  function hexToRgb(hex) {
    let h = String(hex || '#888888').replace('#', '');
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    const n = parseInt(h.slice(0, 6), 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }
  function shade(hex, amt) {
    const [r, g, b] = hexToRgb(hex);
    const f = (c) => Math.max(0, Math.min(255, Math.round(amt < 0 ? c * (1 + amt) : c + (255 - c) * amt)));
    return 'rgb(' + f(r) + ',' + f(g) + ',' + f(b) + ')';
  }
  function isLight(hex) {
    const [r, g, b] = hexToRgb(hex);
    return r * 0.299 + g * 0.587 + b * 0.114 > 170;
  }

  /* ---------- chair back outlines ---------- */
  function backPath(style, cx, top, w, h) {
    const l = cx - w / 2;
    const r = cx + w / 2;
    const b = top + h;
    if (style === 'royal') {
      return (
        `M${l},${b} L${l},${top + h * 0.3} C${l},${top + h * 0.06} ${cx - w * 0.22},${top + h * 0.04} ${cx},${top - 6}` +
        ` C${cx + w * 0.22},${top + h * 0.04} ${r},${top + h * 0.06} ${r},${top + h * 0.3} L${r},${b} Z`
      );
    }
    if (style === 'oval') {
      const ry = h * 0.43;
      const cy = top + ry;
      return `M${l},${cy} A${w / 2},${ry} 0 1 1 ${r},${cy} A${w / 2},${ry} 0 1 1 ${l},${cy} Z`;
    }
    const rad = style === 'modern' ? Math.min(w / 2, 26) : 13;
    return (
      `M${l},${b} L${l},${top + rad} Q${l},${top} ${l + rad},${top} L${r - rad},${top}` +
      ` Q${r},${top} ${r},${top + rad} L${r},${b} Z`
    );
  }

  const BACK_H = { classic: 150, royal: 160, modern: 118, oval: 140 };

  /* one chair seen from the front (front = true) or from behind */
  function chair(style, cx, bottom, s, fabric, wood, accent, front, legs) {
    const w = 74 * s;
    const h = (BACK_H[style] || 140) * s;
    const top = bottom - h;
    const fr = style === 'modern' ? 0 : 7 * s;
    const woodDark = shade(wood, -0.25);
    let out = '';
    if (legs) {
      const ly = bottom;
      out +=
        `<rect x="${cx - w / 2 + 3 * s}" y="${ly - 4}" width="${6 * s}" height="${legs}" rx="2" fill="${woodDark}"/>` +
        `<rect x="${cx + w / 2 - 9 * s}" y="${ly - 4}" width="${6 * s}" height="${legs}" rx="2" fill="${woodDark}"/>`;
    }
    if (style === 'oval') {
      // posts under the medallion
      out +=
        `<rect x="${cx - w / 2 + 6 * s}" y="${top + h * 0.6}" width="${7 * s}" height="${h * 0.42}" fill="${wood}"/>` +
        `<rect x="${cx + w / 2 - 13 * s}" y="${top + h * 0.6}" width="${7 * s}" height="${h * 0.42}" fill="${wood}"/>`;
    }
    const frontFabric = front ? fabric : shade(fabric, -0.12);
    if (fr) {
      out += `<path d="${backPath(style, cx, top, w, style === 'oval' ? h * 0.86 : h)}" fill="${wood}" stroke="${woodDark}" stroke-width="1"/>`;
      out += `<path d="${backPath(style, cx, top + fr, w - fr * 2, (style === 'oval' ? h * 0.86 : h) - fr * 2 + (style === 'oval' ? 0 : fr))}" fill="${frontFabric}"/>`;
    } else {
      out += `<path d="${backPath(style, cx, top, w, h)}" fill="${frontFabric}" stroke="${shade(fabric, -0.3)}" stroke-width="1.2"/>`;
    }
    if (accent && fr) {
      out += `<path d="${backPath(style, cx, top + 2 * s, w - 4 * s, (style === 'oval' ? h * 0.86 : h) - 2 * s)}" fill="none" stroke="${accent}" stroke-width="${1.6 * s}"/>`;
    }
    if (front) {
      const dot = shade(fabric, -0.28);
      if (style === 'classic' || style === 'royal') {
        for (let row = 0; row < 3; row++) {
          for (let col = 0; col < 3 - (row % 2); col++) {
            const x = cx - 18 * s + col * 18 * s + (row % 2 ? 9 * s : 0);
            const y = top + (style === 'royal' ? 42 : 30) * s + row * 26 * s;
            out += `<circle cx="${x}" cy="${y}" r="${1.8 * s}" fill="${dot}"/>`;
          }
        }
      } else if (style === 'modern') {
        for (let i = -1; i <= 1; i++) {
          out += `<line x1="${cx + i * 17 * s}" y1="${top + 14 * s}" x2="${cx + i * 17 * s}" y2="${bottom - 6}" stroke="${dot}" stroke-width="${1.4 * s}" opacity=".7"/>`;
        }
      } else if (style === 'oval') {
        out += `<circle cx="${cx}" cy="${top + h * 0.37}" r="${2 * s}" fill="${dot}"/>`;
      }
    }
    return out;
  }

  /* side profile chair at a table end; dir = 1 faces right, -1 faces left */
  function sideChair(style, x, seatY, s, fabric, wood, accent, dir) {
    const woodDark = shade(wood, -0.25);
    const backH = (BACK_H[style] || 140) * 0.92 * s;
    const bx = x - dir * 30 * s;
    let out = '';
    // legs
    out += `<rect x="${bx - 4 * s}" y="${seatY}" width="${6 * s}" height="${445 - seatY}" fill="${woodDark}"/>`;
    out += `<rect x="${x + dir * 26 * s - 3 * s}" y="${seatY}" width="${6 * s}" height="${445 - seatY}" fill="${woodDark}"/>`;
    // back
    const bw = 13 * s;
    const top = seatY - backH;
    const crest = style === 'royal' ? 8 : style === 'oval' ? 4 : 0;
    out += `<path d="M${bx - bw / 2},${seatY + 4} L${bx - bw / 2 - dir * 4},${top + crest} Q${bx - dir * 2},${top - crest} ${bx + bw / 2 - dir * 4},${top + crest} L${bx + bw / 2},${seatY + 4} Z" fill="${style === 'modern' ? fabric : wood}" stroke="${woodDark}" stroke-width="1"/>`;
    if (style !== 'modern') {
      out += `<path d="M${bx + dir * (bw / 2 - 1)},${seatY - 4} L${bx + dir * (bw / 2 - 1) - dir * 3},${top + 14 * s} L${bx + dir * (bw / 2 + 3)},${top + 14 * s} L${bx + dir * (bw / 2 + 5)},${seatY - 4} Z" fill="${fabric}"/>`;
    }
    // seat
    const sx1 = Math.min(bx, x + dir * 32 * s);
    out += `<rect x="${sx1 - 4}" y="${seatY - 12 * s}" width="${Math.abs(x + dir * 32 * s - bx) + 8}" height="${16 * s}" rx="${6 * s}" fill="${fabric}" stroke="${shade(fabric, -0.25)}" stroke-width="1"/>`;
    out += `<rect x="${sx1 - 4}" y="${seatY + 3 * s}" width="${Math.abs(x + dir * 32 * s - bx) + 8}" height="${5 * s}" fill="${accent || wood}"/>`;
    return out;
  }

  function topFill(id, top, wood) {
    if (top === 'marble') {
      return {
        defs: `<linearGradient id="${id}" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#fbfaf7"/><stop offset=".55" stop-color="#ece8e1"/><stop offset="1" stop-color="#f6f3ee"/></linearGradient>`,
        fill: `url(#${id})`,
        veins: '#b9b2a8',
        edge: '#d9d3ca',
      };
    }
    if (top === 'black') {
      return {
        defs: `<linearGradient id="${id}" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#2a2725"/><stop offset=".6" stop-color="#151413"/><stop offset="1" stop-color="#262321"/></linearGradient>`,
        fill: `url(#${id})`,
        veins: '#c4a46b',
        edge: '#0d0c0b',
      };
    }
    if (top === 'glass') {
      return {
        defs: `<linearGradient id="${id}" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stop-color="#cfe3e4" stop-opacity=".55"/><stop offset=".5" stop-color="#eef7f7" stop-opacity=".35"/><stop offset="1" stop-color="#cfe3e4" stop-opacity=".55"/></linearGradient>`,
        fill: `url(#${id})`,
        veins: null,
        edge: 'rgba(150,185,188,.8)',
        glass: true,
      };
    }
    return {
      defs: `<linearGradient id="${id}" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stop-color="${shade(wood, -0.08)}"/><stop offset=".5" stop-color="${shade(wood, 0.1)}"/><stop offset="1" stop-color="${shade(wood, -0.1)}"/></linearGradient>`,
      fill: `url(#${id})`,
      grain: shade(wood, -0.22),
      edge: shade(wood, -0.3),
    };
  }

  /* ---------- dining set ---------- */
  function dining(o) {
    o = Object.assign(
      { seats: 6, shape: 'rect', chair: 'classic', fabric: '#d8c6a8', wood: '#5b3a24', top: 'wood', accent: '' },
      o || {}
    );
    const uid = 'rqd' + uidSeed++;
    const seats = Math.max(2, parseInt(o.seats, 10) || 6);
    const accent = o.accent && o.accent !== 'none' ? o.accent : '';
    const round = o.shape === 'round';
    const ends = seats >= 6 ? 2 : 0;
    const perSide = Math.ceil((seats - ends) / 2);
    const spacing = round ? 92 : 104;
    const cx = 400;
    const backW = perSide * spacing + 30;
    const frontW = backW + 70;
    const yBack = 252;
    const yFront = 302;
    const woodDark = shade(o.wood, -0.3);
    const tf = topFill(uid + 't', o.top, o.wood);
    let defs = tf.defs;
    defs += `<radialGradient id="${uid}s" cx=".5" cy=".5" r=".5"><stop offset="0" stop-color="#2b1a10" stop-opacity=".28"/><stop offset="1" stop-color="#2b1a10" stop-opacity="0"/></radialGradient>`;
    let svg = '';

    svg += `<ellipse cx="${cx}" cy="458" rx="${frontW / 2 + 130}" ry="34" fill="url(#${uid}s)"/>`;

    const xs = [];
    for (let i = 0; i < perSide; i++) xs.push(cx - ((perSide - 1) / 2) * spacing + i * spacing);

    // back row chairs (front side visible)
    xs.forEach((x) => (svg += chair(o.chair, x, yBack + 18, 1, o.fabric, o.wood, accent, true, 0)));

    // back legs
    const bl = backW / 2 - 18;
    if (!round && o.shape !== 'oval') {
      svg += `<rect x="${cx - bl - 4}" y="${yBack}" width="9" height="160" fill="${shade(o.wood, -0.4)}"/>`;
      svg += `<rect x="${cx + bl - 5}" y="${yBack}" width="9" height="160" fill="${shade(o.wood, -0.4)}"/>`;
    }

    // end chairs (behind the table front plane)
    if (ends) {
      svg += sideChair(o.chair, cx - frontW / 2 - 46, 336, 1.08, o.fabric, o.wood, accent, 1);
      svg += sideChair(o.chair, cx + frontW / 2 + 46, 336, 1.08, o.fabric, o.wood, accent, -1);
    }

    // table
    if (o.shape === 'oval' || round) {
      const rx = round ? Math.max(150, frontW / 2 - 10) : frontW / 2 + 10;
      const cy = (yBack + yFront) / 2;
      const ry = round ? 36 : 30;
      // pedestal
      svg += `<path d="M${cx - 26},${cy + 20} L${cx - 34},420 Q${cx},410 ${cx + 34},420 L${cx + 26},${cy + 20} Z" fill="${o.wood}" stroke="${woodDark}"/>`;
      svg += `<path d="M${cx - 34},420 Q${cx - 90},432 ${cx - 120},446 L${cx + 120},446 Q${cx + 90},432 ${cx + 34},420 Z" fill="${shade(o.wood, -0.15)}" stroke="${woodDark}"/>`;
      if (accent) svg += `<rect x="${cx - 30}" y="412" width="60" height="5" fill="${accent}"/>`;
      svg += `<ellipse cx="${cx}" cy="${cy + 13}" rx="${rx}" ry="${ry}" fill="${tf.glass ? 'rgba(150,185,188,.35)' : tf.edge}"/>`;
      svg += `<ellipse cx="${cx}" cy="${cy}" rx="${rx}" ry="${ry}" fill="${tf.fill}" stroke="${tf.edge}" stroke-width="1.5"/>`;
      if (accent) svg += `<ellipse cx="${cx}" cy="${cy + 7}" rx="${rx}" ry="${ry}" fill="none" stroke="${accent}" stroke-width="2" stroke-dasharray="0 ${rx * 1.6} ${rx * 3.4}"/>`;
      if (tf.veins) svg += `<path d="M${cx - rx * 0.6},${cy - 6} q60,14 110,-2 t120,8 M${cx - rx * 0.2},${cy + 14} q50,-12 120,-4" stroke="${tf.veins}" stroke-width="1.2" fill="none" opacity=".8"/>`;
      if (tf.grain) svg += `<path d="M${cx - rx * 0.8},${cy - 4} Q${cx},${cy - 14} ${cx + rx * 0.8},${cy - 4} M${cx - rx * 0.7},${cy + 10} Q${cx},${cy} ${cx + rx * 0.75},${cy + 12}" stroke="${tf.grain}" stroke-width="1" fill="none" opacity=".5"/>`;
    } else {
      const fl = frontW / 2;
      svg += `<rect x="${cx - fl + 22}" y="${yFront + 30}" width="12" height="115" fill="${o.wood}" stroke="${woodDark}"/>`;
      svg += `<rect x="${cx + fl - 34}" y="${yFront + 30}" width="12" height="115" fill="${o.wood}" stroke="${woodDark}"/>`;
      svg += `<rect x="${cx - fl + 10}" y="${yFront + 12}" width="${frontW - 20}" height="22" fill="${shade(o.wood, -0.1)}" stroke="${woodDark}"/>`;
      if (accent) svg += `<rect x="${cx - fl + 10}" y="${yFront + 28}" width="${frontW - 20}" height="3" fill="${accent}"/>`;
      svg += `<path d="M${cx - backW / 2},${yBack} L${cx + backW / 2},${yBack} L${cx + fl},${yFront} L${cx - fl},${yFront} Z" fill="${tf.fill}" stroke="${tf.edge}" stroke-width="1.5"/>`;
      svg += `<rect x="${cx - fl}" y="${yFront}" width="${frontW}" height="13" fill="${tf.glass ? 'rgba(150,185,188,.55)' : tf.edge}"/>`;
      if (tf.veins) svg += `<path d="M${cx - backW / 2 + 30},${yBack + 14} q70,18 140,0 t150,12 M${cx - 60},${yFront - 10} q60,-20 150,-6" stroke="${tf.veins}" stroke-width="1.2" fill="none" opacity=".8"/>`;
      if (tf.grain) {
        for (let i = 1; i <= 3; i++) {
          const y = yBack + i * 12;
          const k = (y - yBack) / (yFront - yBack);
          const half = backW / 2 + (fl - backW / 2) * k;
          svg += `<path d="M${cx - half + 12},${y} Q${cx},${y - 5} ${cx + half - 12},${y + 2}" stroke="${tf.grain}" stroke-width="1" fill="none" opacity=".45"/>`;
        }
      }
    }

    // front row chairs (seen from behind)
    xs.forEach((x) => (svg += chair(o.chair, x, 440, 1, o.fabric, o.wood, accent, false, 12)));

    return `<svg viewBox="0 0 800 500" xmlns="http://www.w3.org/2000/svg" role="img"><defs>${defs}</defs>${svg}</svg>`;
  }

  /* ---------- niche (china cabinet) ---------- */
  function niche(o) {
    o = Object.assign({ modules: 3, crown: 'flat', wood: '#5b3a24', glass: '#dfeef0', handle: '#c4a46b', light: false }, o || {});
    const uid = 'rqn' + uidSeed++;
    const m = Math.max(2, Math.min(5, parseInt(o.modules, 10) || 3));
    const mw = m >= 4 ? 112 : 124;
    const W = m * mw + 20;
    const cx = 400;
    const x0 = cx - W / 2;
    const wood = o.wood;
    const wd = shade(wood, -0.3);
    const wl = shade(wood, 0.12);
    const handle = o.handle || '#c4a46b';
    const glass = o.glass || '#dfeef0';
    const lightOn = o.light === true || o.light === 'on' || o.light === 'yes';
    const hutchTop = o.crown === 'flat' ? 96 : 104;
    let defs =
      `<linearGradient id="${uid}w" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stop-color="${shade(wood, -0.08)}"/><stop offset=".5" stop-color="${wl}"/><stop offset="1" stop-color="${shade(wood, -0.1)}"/></linearGradient>` +
      `<radialGradient id="${uid}s" cx=".5" cy=".5" r=".5"><stop offset="0" stop-color="#2b1a10" stop-opacity=".3"/><stop offset="1" stop-color="#2b1a10" stop-opacity="0"/></radialGradient>` +
      `<linearGradient id="${uid}in" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="${lightOn ? '#ffe2a8' : shade(wood, -0.45)}"/><stop offset="1" stop-color="${lightOn ? '#c98f4c' : shade(wood, -0.55)}"/></linearGradient>`;
    let svg = `<ellipse cx="${cx}" cy="470" rx="${W / 2 + 110}" ry="26" fill="url(#${uid}s)"/>`;

    // hutch body + interior
    svg += `<rect x="${x0 + 6}" y="${hutchTop}" width="${W - 12}" height="${292 - hutchTop}" fill="url(#${uid}w)" stroke="${wd}"/>`;
    svg += `<rect x="${x0 + 16}" y="${hutchTop + 10}" width="${W - 32}" height="${282 - hutchTop - 10}" fill="url(#${uid}in)"/>`;
    // shelves + dishes
    const shelves = [hutchTop + 70, hutchTop + 132, 280];
    shelves.forEach((sy, si) => {
      if (si < 2) svg += `<rect x="${x0 + 16}" y="${sy}" width="${W - 32}" height="5" fill="${wl}" opacity=".9"/>`;
      for (let i = 0; i < m; i++) {
        const mx = x0 + 10 + i * mw + mw / 2;
        const base = si < 2 ? sy : 280;
        const dish = '#f4efe6';
        if ((i + si) % 2 === 0) {
          svg += `<ellipse cx="${mx - 18}" cy="${base - 24}" rx="20" ry="22" fill="${dish}" stroke="#cfc4b4"/><ellipse cx="${mx - 18}" cy="${base - 24}" rx="12" ry="13" fill="none" stroke="${handle}" stroke-width="1.5"/>`;
          svg += `<ellipse cx="${mx + 14}" cy="${base - 20}" rx="16" ry="18" fill="${dish}" stroke="#cfc4b4"/>`;
        } else {
          svg += `<path d="M${mx - 28},${base - 22} h20 v14 q-10,8 -20,0 Z" fill="${dish}" stroke="#cfc4b4"/><path d="M${mx + 2},${base - 34} q10,-6 20,0 l-2,30 h-16 Z" fill="${dish}" stroke="#cfc4b4"/><rect x="${mx - 4}" y="${base - 46}" width="3" height="46" fill="${handle}" opacity=".7"/>`;
        }
      }
    });
    if (lightOn) {
      for (let i = 0; i < m; i++) {
        const mx = x0 + 10 + i * mw + mw / 2;
        svg += `<circle cx="${mx}" cy="${hutchTop + 16}" r="3" fill="#fff6d8"/><path d="M${mx - 3},${hutchTop + 18} L${mx - 34},${hutchTop + 66} L${mx + 34},${hutchTop + 66} L${mx + 3},${hutchTop + 18} Z" fill="#fff1c4" opacity=".35"/>`;
      }
    }
    // glass doors
    for (let i = 0; i < m; i++) {
      const dx = x0 + 10 + i * mw + 4;
      const dw = mw - 8;
      svg += `<rect x="${dx}" y="${hutchTop + 8}" width="${dw}" height="${282 - hutchTop - 8}" fill="${glass}" fill-opacity="${isLight(glass) ? 0.28 : 0.42}" stroke="${wood}" stroke-width="7"/>`;
      svg += `<path d="M${dx + 14},${hutchTop + 30} l26,-14 M${dx + 14},${hutchTop + 52} l46,-26" stroke="#fff" stroke-opacity=".45" stroke-width="2"/>`;
      const hx = i % 2 === 0 ? dx + dw - 10 : dx + 6;
      svg += `<rect x="${hx}" y="${hutchTop + 110}" width="4" height="34" rx="2" fill="${handle}"/>`;
    }
    // crown
    if (o.crown === 'arch') {
      svg += `<path d="M${x0 - 4},${hutchTop} L${x0 - 4},${hutchTop - 14} L${cx - W * 0.22},${hutchTop - 14} Q${cx},${hutchTop - 64} ${cx + W * 0.22},${hutchTop - 14} L${x0 + W + 4},${hutchTop - 14} L${x0 + W + 4},${hutchTop} Z" fill="url(#${uid}w)" stroke="${wd}"/>`;
      svg += `<path d="M${cx - W * 0.2},${hutchTop - 10} Q${cx},${hutchTop - 56} ${cx + W * 0.2},${hutchTop - 10}" fill="none" stroke="${handle}" stroke-width="2"/>`;
    } else if (o.crown === 'crown') {
      svg += `<rect x="${x0 - 10}" y="${hutchTop - 18}" width="${W + 20}" height="18" fill="url(#${uid}w)" stroke="${wd}"/>`;
      svg += `<rect x="${x0 - 4}" y="${hutchTop - 4}" width="${W + 8}" height="4" fill="${handle}"/>`;
      svg += `<path d="M${cx - 70},${hutchTop - 18} C${cx - 60},${hutchTop - 40} ${cx - 30},${hutchTop - 30} ${cx - 20},${hutchTop - 46} Q${cx},${hutchTop - 76} ${cx + 20},${hutchTop - 46} C${cx + 30},${hutchTop - 30} ${cx + 60},${hutchTop - 40} ${cx + 70},${hutchTop - 18} Z" fill="url(#${uid}w)" stroke="${wd}"/>`;
      svg += `<circle cx="${cx}" cy="${hutchTop - 40}" r="7" fill="${handle}"/><path d="M${cx - 52},${hutchTop - 22} Q${cx},${hutchTop - 34} ${cx + 52},${hutchTop - 22}" fill="none" stroke="${handle}" stroke-width="1.6"/>`;
    } else {
      svg += `<rect x="${x0 - 6}" y="${hutchTop - 14}" width="${W + 12}" height="14" fill="url(#${uid}w)" stroke="${wd}"/>`;
    }

    // base cabinet
    svg += `<rect x="${x0 - 8}" y="292" width="${W + 16}" height="12" fill="${wl}" stroke="${wd}"/>`;
    svg += `<rect x="${x0}" y="304" width="${W}" height="128" fill="url(#${uid}w)" stroke="${wd}"/>`;
    for (let i = 0; i < m; i++) {
      const dx = x0 + 10 + i * mw;
      svg += `<rect x="${dx + 4}" y="310" width="${mw - 8}" height="28" fill="${shade(wood, 0.04)}" stroke="${wd}"/>`;
      svg += `<rect x="${dx + mw / 2 - 16}" y="322" width="32" height="4" rx="2" fill="${handle}"/>`;
      svg += `<rect x="${dx + 4}" y="342" width="${mw - 8}" height="84" fill="${shade(wood, 0.02)}" stroke="${wd}"/>`;
      svg += `<rect x="${dx + 14}" y="352" width="${mw - 28}" height="64" fill="none" stroke="${wd}" stroke-opacity=".6"/>`;
      const kx = i % 2 === 0 ? dx + mw - 18 : dx + 16;
      svg += `<circle cx="${kx}" cy="384" r="4" fill="${handle}"/>`;
    }
    svg += `<rect x="${x0 - 4}" y="432" width="${W + 8}" height="12" fill="${wd}"/>`;
    svg += `<path d="M${x0 + 4},444 l6,20 h8 l2,-20 Z M${x0 + W - 4},444 l-6,20 h-8 l-2,-20 Z" fill="${wd}"/>`;
    if (o.crown === 'crown') svg += `<rect x="${x0}" y="300" width="${W}" height="3" fill="${handle}"/>`;

    return `<svg viewBox="0 0 800 500" xmlns="http://www.w3.org/2000/svg" role="img"><defs>${defs}</defs>${svg}</svg>`;
  }

  function chairIcon(style, fabric, wood) {
    const body = chair(style || 'classic', 50, 120, 0.62, fabric || '#d8c6a8', wood || '#5b3a24', '', true, 26);
    const seat = `<rect x="22" y="112" width="56" height="12" rx="5" fill="${fabric || '#d8c6a8'}" stroke="${shade(fabric || '#d8c6a8', -0.25)}"/>`;
    return `<svg viewBox="0 0 100 150" xmlns="http://www.w3.org/2000/svg">${body}${seat}</svg>`;
  }

  window.RonaqDraw = { dining, niche, chairIcon, shade };
})();
